/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../base.component';
import { Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { LanguageService, UtilsService } from '../../../services';
import { nameValidation } from '../../../directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-roles',
    templateUrl: './rbac-roles.component.html',
    styleUrls: ['./rbac-roles.component.scss']
})

export class RbacRolesComponent extends BaseComponent {
    /**
     * Get all the steps from the view, so we can scroll to the active step.
     */
    @ViewChildren('step')
    public steps: QueryList<any> = new QueryList<any>();

    /**
     * Incoming data from parent component.
     */
    @Input() set data(data: {
        users: any,
        roles: any,
        groups: any,
        isDuplicate: boolean,
        endpoints: any,
        rolePatch?: any
    }) {
        this.setUpPageData(data);
    }

    /**
     * Incoming list of groups for the endpoints (optional).
     * e.g. [{ path: '/api/v1/:namespace/adcs/', friendlyName: 'ADCs' }]
     * The above example will group all endpoints whose path starts with '/api/v1/:namespace/adcs/'
     * and will add the friendly as an accordion title.
     */
    @Input() public grouping: any[] = [];

    /**
     * Tell parent to submit.
     */
    @Output() public submitRole: EventEmitter<any> = new EventEmitter();

    /**
     * Store if we are editing or not.
     */
    public editId: number = null;

    /**
     * Is in duplicate mode.
     */
    public isDuplicate: boolean;

    /**
     * Store the current step.
     */
    public activeStep: number = 1;

    /**
     * The main formGroup variable.
     */
    public formGroup: FormGroup;

    /**
     * Stored endpoints.
     */
    public endpoints: any[] = [];

    /**
     * Store the list of existing roles to check for duplicate names.
     * @private
     */
    private storedRolesName: any [] = [];

    /**
     * To store the initial IP form data.
     * @private
     */
    private initialFormData: object = {};

    constructor(
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private utilsService: UtilsService
    ) {
        super();

        this.setupFormGroup();
        this.setUpFormSubscribers();
    }

    /**
     * Go back a step in the process.
     */
    public backStep(): void {
        this.activeStep--;
        this.scrollToActiveStep();
    }

    /**
     * Go forward a step in the process.
     */
    public nextStep(): void {
        this.activeStep++;
        this.scrollToActiveStep();
    }

    /**
     * Go to a specific step in the process.
     * @param number
     */
    public setStep(number: number): void {
        this.activeStep = number;
        this.scrollToActiveStep();
    }

    /**
     * Set back button disabled state.
     */
    public isBackButtonDisabled(): boolean {
        return this.activeStep === 1;
    }

    /**
     * Check if the button is disabled based on the form step state.
     */
    public isNextButtonDisabled(): boolean {
        if (this.editId) {
            if (this.activeStep === 2) {
                return this.formGroup.get('step2.endpoints').invalid;
            }

            return false;
        }

        return this.formGroup.get('step' + this.activeStep).invalid || this.formGroup.get('step' + this.activeStep).pristine;
    }

    /**
     * Cancel button route.
     */
    public getCancelButtonRoute(): string {
        return this.isDuplicate ? '../../../../list' : this.editId ? '../../../list' : '../../list';
    }

    /**
     * Set endpoints in form.
     * @param $event
     */
    public setEndpointsInForm($event: any): void {
        const { values, clean } = $event;
        const cleanedValues = values.filter((item: any) => Object.values(item?.methods)?.includes(true));
        const endpointsForm: AbstractControl<any, any> = this.formGroup.get('step2.endpoints');
        endpointsForm.setValue(cleanedValues.length ? cleanedValues : null);
        if (clean) {
            endpointsForm.markAsPristine();
        } else {
            endpointsForm.markAsDirty();
        }

        endpointsForm.updateValueAndValidity();
    }

    /**
     * Submit helper for emitter.
     */
    public submitHelper(): void {
        const formValues = this.formGroup.value;
        const rolesValues = {
            id: formValues.step1.id,
            name: formValues.step1.name,
            endpoints: formValues.step2.endpoints,
            groups: formValues.step1.groups,
            users: formValues.step1.users
        };

        // Remove PATCH methods from Endurance (uses PUT only).
        if (this.environment.module === this.modulesEnum.LOADBALANCER) {
            for (const endpoint of rolesValues.endpoints) {
                if (endpoint.methods['PATCH'] && endpoint.methods['PUT']) {
                    delete endpoint.methods['PATCH'];
                }
            }
        }

        this.submitRole.emit(rolesValues);
    }

    /**
     * Set up the form builder.
     * @private
     */
    private setupFormGroup(): void {
        this.formGroup = this.formBuilder.group({
            step1: this.formBuilder.group({
                id: [], // used for edit
                name: [null, [Validators.required, Validators.maxLength(254), nameValidation]],
                duplicateName: [{ value: null, disabled: true }],
                groups: [], // used for edit
                users: [] // used for edit
            }),
            step2: this.formBuilder.group({
                endpoints: [null, Validators.required]
            })
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any): void => {
                    if (JSON.stringify(this.initialFormData) === JSON.stringify(values)) {
                        this.formGroup.markAsPristine();
                    }
                }
            });

        const step1: AbstractControl<any, any> = this.formGroup.get('step1');
        const nameField: AbstractControl<any, any> = step1.get('name');
        this.formGroup.get('step1').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any): void => {
                    this.utilsService.debounce('step1', () => {
                        if (this.storedRolesName.includes(values.name.toLowerCase()) && !nameField.hasError('duplicateRoleName')) {
                            nameField.setErrors({
                                'duplicateRoleName': `${this.languageService.translate('duplicate_name')}`
                            });
                            nameField.markAsTouched();
                        }
                    });
                }
            });
    }

    /**
     * Set up page data.
     * @param data
     * @private
     */
    private setUpPageData(data: any): void {
        this.editId = data.rolePatch?.id ? data.rolePatch.id : null;
        this.isDuplicate = data.isDuplicate;
        this.storedRolesName = this.utilsService.filterPropertyForDuplicateCheck(data.roles, 'name', data.rolePatch);
        this.endpoints = data.endpoints;
        if ((this.editId || this.isDuplicate) && data.rolePatch) {
            this.patchValuesToForm(data.rolePatch);
        }
    }

    /**
     * Patch values to form.
     * @param role
     * @private
     */
    private patchValuesToForm(role: any): void {
        if (this.isDuplicate) {
            delete role.id;
            role.duplicateName = role.name;
            delete role.name;
        }

        const patch: any = {
            step1: {
                id: role.id,
                name: role.name,
                duplicateName: role.duplicateName,
                groups: role.groups,
                users: role.users
            },
            step2: { endpoints: role.endpoints },
        };

        this.initialFormData = patch;
        this.formGroup.patchValue(patch);
        this.formGroup.updateValueAndValidity();

        this.endpoints = role.endpoints;
    }

    /**
     * Scroll to active step.
     * @private
     */
    private scrollToActiveStep(): void {
        const elements: any[] = this.steps.toArray();
        elements[this.activeStep - 1].nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }
}
